import { useEffect, useMemo, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent, ReactElement } from 'react';
import { PauseIcon, PlayIcon } from './icons.js';
import type { VoiceComposerLabels } from './labels.js';
import { SAMPLE_MS } from './useHoldTurn.js';

export function fmtTime(s: number): string {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
}

/**
 * Waveforms del take push-to-talk, calcadas de WhatsApp:
 *
 * - `ScrollingWave` (grabando): historial REAL de amplitud como ventana
 *   deslizante anclada a la derecha — cada barra es una muestra de ~100ms,
 *   conserva su forma y se corre hacia la izquierda con el tiempo (nada de
 *   onda sintética que "baila" con el volumen).
 * - `TakeOverview` (pausado): zoom out — el take ENTERO bucketeado al ancho
 *   disponible, con play/pausa que recorre las barras pintando el progreso.
 *
 * Ambas leen el historial del recorder (`getTakeSamples`) — la transición
 * pausar/reanudar es un swap de componente con animación de zoom (CSS).
 */

/** Ancho de un slot de barra (barra + gap) — tiene que matchear el CSS. */
const SLOT_PX = 6;
/** Barras de la vista completa (zoom out) del take pausado. */
const OVERVIEW_BARS = 40;

/** Altura visual [0..1] de una muestra de amplitud (misma curva que speak). */
function sampleHeight(s: number): number {
  return Math.max(0.06, Math.min(1, Math.sqrt(s) * 2.6));
}

export function ScrollingWave({
  getSamples,
}: {
  getSamples: () => readonly number[];
}): ReactElement {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [view, setView] = useState<{ start: number; heights: number[]; shift: number }>({
    start: 0,
    heights: [],
    shift: 0,
  });
  const lastLenRef = useRef(0);
  const lastPushAtRef = useRef(0);

  useEffect(() => {
    let raf = 0;
    let lastEmit = 0;
    const tick = (now: number): void => {
      const samples = getSamples();
      if (samples.length !== lastLenRef.current) {
        lastLenRef.current = samples.length;
        lastPushAtRef.current = now;
      }
      if (now - lastEmit >= 33) {
        lastEmit = now;
        const width = wrapRef.current?.clientWidth ?? 0;
        const slots = Math.max(8, Math.floor(width / SLOT_PX));
        // La barra recién nacida entra deslizándose desde el borde derecho:
        // el row arranca corrido +1 slot (la nueva queda oculta) y drifta a 0.
        const frac = Math.min(1, (now - lastPushAtRef.current) / SAMPLE_MS);
        const windowSamples = samples.slice(-(slots + 1));
        setView({
          start: samples.length - windowSamples.length,
          heights: windowSamples.map(sampleHeight),
          shift: (1 - frac) * SLOT_PX,
        });
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [getSamples]);

  return (
    <div ref={wrapRef} className="ptt-scrollwave" aria-hidden="true">
      <div
        className="ptt-scrollwave-row"
        style={{ transform: `translateX(${view.shift.toFixed(2)}px)` }}
      >
        {view.heights.map((h, i) => (
          <span key={view.start + i} style={{ height: `${(h * 100).toFixed(1)}%` }} />
        ))}
      </div>
    </div>
  );
}

/** Bucketea el historial completo a `buckets` barras (pico por bucket). */
export function bucketSamples(samples: readonly number[], buckets: number): number[] {
  const out = new Array<number>(buckets).fill(0);
  if (samples.length === 0) return out;
  for (let b = 0; b < buckets; b += 1) {
    const from = Math.floor((b * samples.length) / buckets);
    const to = Math.max(from + 1, Math.floor(((b + 1) * samples.length) / buckets));
    let peak = 0;
    for (let i = from; i < to && i < samples.length; i += 1) {
      peak = Math.max(peak, samples[i] ?? 0);
    }
    out[b] = peak;
  }
  return out;
}

/** Duración usable para el seek/progreso: la real del elemento si es finita
 *  (los blobs de MediaRecorder llegan con Infinity en Chrome), si no el
 *  elapsed del recorder como timebase. */
function effectiveDuration(audio: HTMLAudioElement, fallbackSec: number): number {
  return Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : fallbackSec;
}

/**
 * Vista completa del take pausado, calcada del player de WhatsApp: play/pausa
 * (el MISMO botón alterna) + las barras del audio entero con el progreso
 * pintado y un dot arrastrable para hacer seek + el tiempo a la derecha —
 * duración TOTAL en reposo, posición corriendo desde 0:00 al reproducir.
 * Sin `url` (entorno sin MediaRecorder) muestra sólo las barras y el total.
 */
export function TakeOverview({
  samples,
  url,
  durationSec,
  labels,
}: {
  samples: readonly number[];
  url: string | null;
  durationSec: number;
  labels: Pick<VoiceComposerLabels, 'listenToMe' | 'pauseListening'>;
}): ReactElement {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const waveRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  // Ya se reprodujo / scrubbeó algo: el tiempo muestra la POSICIÓN; en reposo
  // (o al terminar) vuelve a la duración total, como WhatsApp.
  const [engaged, setEngaged] = useState(false);

  useEffect(() => {
    if (url === null) return;
    const audio = new Audio(url);
    audioRef.current = audio;
    // Chrome entrega los blobs de MediaRecorder con duration Infinity (bug
    // conocido) y sin duración finita el seek no anda: forzar un currentTime
    // enorme obliga al browser a computarla; el timeupdate siguiente vuelve a 0.
    const onMeta = (): void => {
      if (audio.duration === Infinity) {
        audio.currentTime = 1e101;
        audio.addEventListener(
          'timeupdate',
          () => {
            audio.currentTime = 0;
          },
          { once: true },
        );
      }
    };
    audio.addEventListener('loadedmetadata', onMeta);
    const onEnded = (): void => {
      setPlaying(false);
      setProgress(0);
      setEngaged(false);
    };
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('loadedmetadata', onMeta);
      audio.removeEventListener('ended', onEnded);
      audio.pause();
      audioRef.current = null;
    };
  }, [url]);

  // Progreso a ~30fps mientras suena; el drag del dot manda mientras dura.
  useEffect(() => {
    if (!playing) return;
    let raf = 0;
    let lastEmit = 0;
    const tick = (now: number): void => {
      const audio = audioRef.current;
      if (audio && !draggingRef.current && now - lastEmit >= 33) {
        lastEmit = now;
        const dur = effectiveDuration(audio, durationSec);
        if (dur > 0) setProgress(Math.min(1, audio.currentTime / dur));
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, durationSec]);

  const toggle = (): void => {
    const audio = audioRef.current;
    if (audio === null) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
      return;
    }
    audio.play().catch((err: unknown) => {
      console.warn('[voice-composer] no se pudo reproducir el preview local', err);
      setPlaying(false);
    });
    setPlaying(true);
    setEngaged(true);
  };

  const seekToClientX = (clientX: number): void => {
    const el = waveRef.current;
    const audio = audioRef.current;
    if (el === null || audio === null) return;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0) return;
    const frac = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const dur = effectiveDuration(audio, durationSec);
    if (dur > 0) audio.currentTime = frac * dur;
    setProgress(frac);
    setEngaged(true);
  };

  const seekHandlers =
    url === null
      ? {}
      : {
          onPointerDown: (e: ReactPointerEvent<HTMLDivElement>): void => {
            draggingRef.current = true;
            try {
              e.currentTarget.setPointerCapture(e.pointerId);
            } catch {
              /* noop (jsdom) */
            }
            seekToClientX(e.clientX);
          },
          onPointerMove: (e: ReactPointerEvent<HTMLDivElement>): void => {
            if (draggingRef.current) seekToClientX(e.clientX);
          },
          onPointerUp: (): void => {
            draggingRef.current = false;
          },
          onPointerCancel: (): void => {
            draggingRef.current = false;
          },
        };

  // `samples` llega como snapshot inmutable (identidad estable durante la
  // pausa) — el memo evita re-bucketear en cada tick del RAF de progreso.
  const bars = useMemo(() => bucketSamples(samples, OVERVIEW_BARS), [samples]);
  const playedUpTo = progress * OVERVIEW_BARS;

  return (
    <>
      {url !== null ? (
        <button
          type="button"
          className="vc-takeplay"
          onClick={toggle}
          aria-label={playing ? labels.pauseListening : labels.listenToMe}
        >
          {playing ? <PauseIcon width={17} height={17} /> : <PlayIcon width={19} height={19} />}
        </button>
      ) : null}
      <div
        ref={waveRef}
        className={'ptt-wave ptt-overview' + (url !== null ? ' is-seekable' : '')}
        aria-hidden="true"
        {...seekHandlers}
      >
        {bars.map((s, i) => (
          <span
            key={i}
            className={i < playedUpTo ? 'is-played' : undefined}
            style={{ height: `${(sampleHeight(s) * 100).toFixed(1)}%` }}
          />
        ))}
        {url !== null ? (
          <span className="ptt-seekdot" style={{ left: `${(progress * 100).toFixed(2)}%` }} />
        ) : null}
      </div>
      <span className="ptt-time">{fmtTime(engaged ? progress * durationSec : durationSec)}</span>
    </>
  );
}
